import { createBrowserRouter, Navigate, useParams, type RouteObject } from "react-router";

import { RequireAdmin, RequireAuth } from "../features/auth/RequireAuth";
import { AppShell } from "../features/shell/AppShell";
import { NotFound, RouteError } from "./Fallbacks";

/** The Knowledge Studio is now part of Settings; a link to one knowledge base opens it there. */
function LegacyStudioRedirect() {
  const { kbId } = useParams();
  return <Navigate to={kbId ? `/settings?kb=${encodeURIComponent(kbId)}#knowledge` : "/settings#knowledge"} replace />;
}

/**
 * Pages load on demand, so the first screen ships only what it needs. There is no setup
 * screen: the services are configured on the server. Everyone lands on the public landing page
 * at "/", and someone already signed in goes on to Chat (LandingGate). Old Setup links go to
 * Chat too, and so do links to the removed Tickets page (tickets are in Jira). Every other page
 * needs sign-in (RequireAuth); the landing and sign-in pages are the only ones outside it.
 * Dashboard and Settings are the admin's (RequireAdmin): an account with the user role has Chat
 * alone.
 */
export const routes: RouteObject[] = [
  {
    path: "/",
    errorElement: <RouteError />,
    lazy: async () => ({ Component: (await import("../features/landing/LandingGate")).LandingGate }),
  },
  {
    path: "login",
    errorElement: <RouteError />,
    lazy: async () => ({ Component: (await import("../features/auth/LoginPage")).LoginPage }),
  },
  {
    element: <RequireAuth />,
    errorElement: <RouteError />,
    children: [
      {
        element: <AppShell />,
        errorElement: <RouteError />,
        children: [
          { path: "chat", lazy: async () => ({ Component: (await import("../features/chat/ChatPage")).ChatPage }) },
          {
            element: <RequireAdmin />,
            children: [
              { path: "dashboard", lazy: async () => ({ Component: (await import("../features/dashboard/DashboardPage")).DashboardPage }) },
              { path: "settings", lazy: async () => ({ Component: (await import("../features/settings/SettingsPage")).SettingsPage }) },
              { path: "studio", element: <LegacyStudioRedirect /> },
              { path: "studio/:kbId", element: <LegacyStudioRedirect /> },
            ],
          },
          { path: "setup/*", element: <Navigate to="/chat" replace /> },
          { path: "tickets", element: <Navigate to="/chat" replace /> },
          { path: "search", element: <Navigate to="/chat" replace /> },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];

export function createAppRouter() {
  return createBrowserRouter(routes);
}
